// src/components/LoadWizardDialog.jsx
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Typography,
    Box,
    Chip,
    TextField,
    InputAdornment,
    Divider,
    CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import LaunchIcon from '@mui/icons-material/Launch';
import EditIcon from '@mui/icons-material/Edit';

const LoadWizardDialog = ({ open, onClose, onLoad, wizardsList, onDelete, loading }) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredWizards = wizardsList.filter(wizard =>
        wizard.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        wizard.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (e) {
            return 'Invalid date';
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Load Existing Wizard</Typography>
                    <Chip
                        label={`${wizardsList.length} wizards`}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                </Box>
            </DialogTitle>

            <DialogContent>
                <TextField
                    fullWidth
                    placeholder="Search wizards by name or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    margin="normal"
                    disabled={loading}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        )
                    }}
                />

                <Divider sx={{ my: 2 }} />

                {loading ? (
                    <Box textAlign="center" py={4}>
                        <CircularProgress />
                        <Typography sx={{ mt: 2 }}>Loading wizards...</Typography>
                    </Box>
                ) : filteredWizards.length === 0 ? (
                    <Box textAlign="center" py={4}>
                        <Typography color="text.secondary">
                            {searchTerm ? 'No wizards found matching your search.' : 'No wizards saved yet.'}
                        </Typography>
                    </Box>
                ) : (
                    <List>
                        {filteredWizards.map((wizard) => (
                            <ListItem
                                key={wizard.id}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    mb: 1,
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                        cursor: 'pointer'
                                    }
                                }}
                                onClick={() => !loading && onLoad(wizard.id)}
                            >
                                <Box sx={{ flexGrow: 1 }}>
                                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                        <Typography variant="subtitle1" fontWeight={500}>
                                            {wizard.name}
                                        </Typography>
                                    </Box>

                                    {wizard.description && (
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            {wizard.description}
                                        </Typography>
                                    )}
                                </Box>

                                <ListItemSecondaryAction>
                                    <IconButton
                                        edge="end"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onLoad(wizard.id);
                                            onClose();
                                        }}
                                        title="Load and Edit"
                                        disabled={loading}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        edge="end"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const previewData = {
                                                wizardId: wizard.id,
                                                schema: wizard.jsonSchema,
                                                name: wizard.name
                                            };
                                            localStorage.setItem('previewWizard', JSON.stringify(previewData));
                                            window.open('/wizard/' + wizard.id, '_blank');
                                        }}
                                        title="Preview"
                                        disabled={loading}
                                    >
                                        <LaunchIcon />
                                    </IconButton>
                                    <IconButton
                                        edge="end"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (window.confirm(`Delete "${wizard.name}"?`)) {
                                                onDelete(wizard.id);
                                            }
                                        }}
                                        title="Delete"
                                        color="error"
                                        disabled={loading}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
};

export default LoadWizardDialog;